import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/**
 * Primitivas de UI no formato do shadcn/ui (cva + forwardRef + `cn`), escritas à
 * mão para não arrastar o gerador inteiro nesta fatia. Componentes do shadcn
 * podem ser adicionados depois sem conflito, porque a API é a mesma.
 */

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Exportado para dar aparência de botão a um `<Link>` sem trazer Radix Slot. */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-all ' +
    'disabled:pointer-events-none disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2',
  {
    variants: {
      variant: {
        primary:
          'bg-brand-600 text-white shadow-sm hover:bg-brand-700 active:bg-brand-800 outline-brand-600',
        accent:
          'bg-accent-400 text-white shadow-sm hover:bg-accent-500 active:bg-accent-600 outline-accent-500',
        outline:
          'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 outline-brand-600',
        ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 outline-brand-600',
        link: 'text-brand-600 underline-offset-4 hover:underline outline-brand-600',
        // Só para o que não tem volta. Vermelho em botão comum vira ruído e
        // deixa de significar perigo justamente onde ele importa.
        danger:
          'bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800 outline-red-600',
      },
      size: {
        sm: 'h-8 px-3',
        md: 'h-10 px-4',
        lg: 'h-11 px-6 text-base',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /**
   * Ícone dentro do campo, à esquerda. É um slot do componente, e não algo
   * posicionado por quem usa: o rótulo fica acima do input, então um
   * `absolute top-1/2` de fora centralizaria o ícone sobre os dois.
   */
  icon?: ReactNode;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, hint, className, id, icon, ...props },
  ref,
) {
  const inputId = id ?? props.name ?? label;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="relative">
        {icon && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-slate-400"
          >
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={cn(hint && hintId, error && errorId) || undefined}
          className={cn(
            'w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors',
            'placeholder:text-slate-400 focus:outline-2 focus:outline-offset-0',
            icon && 'pl-9',
            error
              ? 'border-red-400 focus:outline-red-500'
              : 'border-slate-300 focus:border-brand-500 focus:outline-brand-500',
            className,
          )}
          {...props}
        />
      </div>
      {hint && !error && (
        <p id={hintId} className="text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  );
});

export function Alert({
  variant = 'error',
  children,
}: {
  variant?: 'error' | 'success' | 'info';
  children: ReactNode;
}) {
  const styles = {
    error: 'border-red-200 bg-red-50 text-red-800',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    info: 'border-brand-200 bg-brand-50 text-brand-800',
  } as const;

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn('rounded-lg border px-3 py-2.5 text-sm', styles[variant])}
    >
      {children}
    </div>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-slate-200 bg-white p-6 shadow-sm ring-1 ring-slate-900/[0.02]',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Placeholder de carregamento — evita o "pulo" de layout ao chegar o dado. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-slate-200', className)} />;
}

// ---------------------------------------------------------------------------
// Primitivas da fatia (b) — cadastro e ficha do fornecedor
// ---------------------------------------------------------------------------

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-slate-100 text-slate-700',
        brand: 'bg-brand-50 text-brand-700 ring-1 ring-brand-100',
        accent: 'bg-accent-50 text-accent-700 ring-1 ring-accent-100',
        success: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
        warning: 'bg-amber-50 text-amber-800 ring-1 ring-amber-100',
        danger: 'bg-red-50 text-red-700 ring-1 ring-red-100',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export function Badge({
  tone,
  className,
  children,
}: VariantProps<typeof badgeVariants> & { className?: string; children: ReactNode }) {
  return <span className={cn(badgeVariants({ tone }), className)}>{children}</span>;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, hint, className, id, children, ...props },
  ref,
) {
  const selectId = id ?? props.name ?? label;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-slate-700">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        className={cn(
          'w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors',
          'focus:outline-2 focus:outline-offset-0',
          error
            ? 'border-red-400 focus:outline-red-500'
            : 'border-slate-300 focus:border-brand-500 focus:outline-brand-500',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

/**
 * Campo de texto longo que **cresce com o conteúdo**.
 *
 * `rows` vira o mínimo, não o teto. No papel isso não é conforto: um `rows={3}`
 * com dez linhas digitadas imprime três e perde sete, sem aviso — o mesmo tipo
 * de perda silenciosa que os `<details>` fechados causavam no relatório. Como o
 * 8D é impresso e circula em auditoria, o texto tem de caber inteiro.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, hint, className, id, ...props },
  ref,
) {
  const textareaId = id ?? props.name ?? label;
  const interno = useRef<HTMLTextAreaElement | null>(null);

  // `auto` antes de medir: sem isso a altura só cresce, e apagar texto deixaria
  // um campo alto e vazio.
  //
  // A borda entra na conta porque o `box-sizing` é `border-box`: `scrollHeight`
  // mede conteúdo mais espaçamento, e atribuí-lo direto a `height` esconde
  // exatamente a espessura das duas bordas. São dois pixels — o suficiente para
  // decepar a perna do "g" na última linha impressa.
  const ajustar = useCallback((elemento: HTMLTextAreaElement | null) => {
    if (!elemento) return;
    elemento.style.height = 'auto';
    const estilo = getComputedStyle(elemento);
    const bordas =
      parseFloat(estilo.borderTopWidth || '0') + parseFloat(estilo.borderBottomWidth || '0');
    elemento.style.height = `${elemento.scrollHeight + bordas}px`;
  }, []);

  useEffect(() => {
    ajustar(interno.current);
  }, [ajustar, props.value]);

  return (
    <div className="space-y-1.5">
      <label htmlFor={textareaId} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <textarea
        ref={(elemento) => {
          interno.current = elemento;
          if (typeof ref === 'function') ref(elemento);
          else if (ref) ref.current = elemento;
          ajustar(elemento);
        }}
        id={textareaId}
        aria-invalid={error ? true : undefined}
        onInput={(evento) => ajustar(evento.currentTarget)}
        className={cn(
          'w-full resize-none overflow-hidden rounded-lg border bg-white px-3 py-2 text-sm',
          'text-slate-900 shadow-sm transition-colors',
          'placeholder:text-slate-400 focus:outline-2 focus:outline-offset-0',
          error
            ? 'border-red-400 focus:outline-red-500'
            : 'border-slate-300 focus:border-brand-500 focus:outline-brand-500',
          className,
        )}
        {...props}
      />
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
});

/**
 * Modal com backdrop desfocado.
 *
 * Fecha no `Esc` e no clique fora, e devolve o foco ao fechar — quem usa
 * teclado em auditoria não pode ficar preso atrás de um diálogo.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: 'md' | 'lg';
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Trava a rolagem do fundo: sem isso o conteúdo atrás rola junto no celular.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'animate-rise relative max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl',
          size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg',
        )}
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-600">{description}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

/** Estado vazio: diz o que aconteceu e o que fazer, nunca só "sem dados". */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Cabeçalho de coluna ordenável.
 *
 * A seta só aparece na coluna ativa. Uma seta cinza em toda coluna avisaria que
 * dá para clicar, mas transformaria o cabeçalho num campo de setas onde a
 * ordenação vigente — que é a informação que importa — se perde no meio.
 *
 * `aria-sort` é o que faz o leitor de tela anunciar "coluna ordenada de forma
 * crescente" em vez de ler um botão sem estado.
 */
export function SortHeader<K extends string>({
  column,
  sort,
  onSort,
  align = 'left',
  className,
  children,
}: {
  column: K;
  sort: { key: K; direction: 'asc' | 'desc' };
  onSort: (key: K) => void;
  align?: 'left' | 'right';
  className?: string;
  children: ReactNode;
}) {
  const ativa = sort.key === column;
  return (
    <th
      scope="col"
      aria-sort={ativa ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('px-4 py-3 font-medium', align === 'right' && 'text-right', className)}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 rounded uppercase tracking-wide transition-colors',
          'hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2',
          ativa ? 'text-slate-700' : 'text-slate-500',
        )}
      >
        {children}
        {ativa &&
          (sort.direction === 'asc' ? (
            <ArrowUp className="size-3" aria-hidden />
          ) : (
            <ArrowDown className="size-3" aria-hidden />
          ))}
      </button>
    </th>
  );
}
